// 分判商單: 分判商單 + 附件上傳 (PDF/相片)
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, auth, money, fmtDate, todayStr, PO_STATUS, r2 } from '../api.js';
import { Modal, Field, StatusBadge, Loading, Empty, PageHead, ItemsEditor, useToast, useConfirm } from '../ui.jsx';

const MAX_MB = 20;

function POEditor({ orders, suppliers, catalog, initial, presetOrder, onClose, onSaved }) {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const isNew = !initial?.id;
  const [f, setF] = useState({
    order_id: initial?.order_id || presetOrder || '',
    supplier_id: initial?.supplier_id || '',
    po_date: initial?.po_date || todayStr(),
    delivery_date: initial?.delivery_date || '',
    discount_type: initial?.discount_type || 'none',
    discount_value: initial?.discount_value || 0,
    notes: initial?.notes || '',
    status: initial?.status || 'issued',
    items: initial?.items || [{ item_id: null, code: '', description: '', qty: 1, unit: '項', unit_price: 0 }],
    total_override: initial?.source === 'upload' ? (initial?.total ?? '') : '',
  });
  const [files, setFiles] = useState([]); // {filename, mime, data_base64}
  const [existAtts, setExistAtts] = useState(initial?.attachments || []);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const subtotal = f.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);

  const pickFile = (e) => {
    const list = [...e.target.files];
    const ok = [];
    for (const file of list) {
      if (!/^(application\/pdf|image\/)/i.test(file.type)) { toast(`${file.name}: 只支援 PDF 或圖片`, 'err'); continue; }
      if (file.size > MAX_MB * 1024 * 1024) { toast(`${file.name}: 超過 ${MAX_MB}MB`, 'err'); continue; }
      const rd = new FileReader();
      rd.onload = () => ok.push({ filename: file.name, mime: file.type, data_base64: rd.result }) && setFiles([...ok]);
      rd.readAsDataURL(file);
    }
    e.target.value = '';
  };
  const uploadExist = async () => {
    for (const file of files) {
      try { await api.post(`/pos/${initial.id}/attachments`, file); } catch (e) { toast(`${file.filename}: ${e.message}`, 'err'); }
    }
  };

  const save = async () => {
    if (!f.supplier_id) return toast('請選擇供應商', 'err');
    if (!f.order_id) return toast('⚠️ 分判商單必須選擇關聯工程 (Order)', 'err');
    if (f.total_override === '' && !f.items.some(it => it.description.trim())) return toast('請加入分判項目，或改用「上傳 PO」直接填總額', 'err');
    setBusy(true);
    try {
      const body = {
        ...f,
        items: f.items.filter(it => it.description.trim()),
        order_id: Number(f.order_id), supplier_id: Number(f.supplier_id),
        total_override: f.total_override === '' ? null : Number(f.total_override),
      };
      let pid = initial?.id;
      if (isNew) { const r = await api.post('/pos', body); pid = r.id; }
      else await api.put(`/pos/${pid}`, body);
      for (const file of files) {
        try { await api.post(`/pos/${pid}/attachments`, file); } catch (e) { toast(`${file.filename}: ${e.message}`, 'err'); }
      }
      toast('分判商單已儲存 ✔');
      onSaved(pid);
    } catch (e) { toast(e.message, 'err'); } finally { setBusy(false); }
  };

  const delAtt = async (a) => {
    try { await api.del(`/attachments/${a.id}`); setExistAtts(l => l.filter(x => x.id !== a.id)); toast('附件已刪除'); } catch (e) { toast(e.message, 'err'); }
  };

  return (
    <Modal size="xl" onClose={onClose} icon="📦" title={isNew ? '➕ 新增分判商單 (分判商單)' : `✏️ 編輯分判商單 ${initial.po_no}`}
      footer={<><button className="btn" onClick={onClose}>取消</button><button className="btn primary" disabled={busy} onClick={save}>{busy ? '儲存中…' : '💾 儲存分判商單'}</button></>}>
      <div className="form-grid mb">
        <Field label="關聯工程 Order (必須 — 成本入落正確 Project)" required>
          <select className="select" value={f.order_id} onChange={e => set('order_id', e.target.value)}>
            <option value="">— 必須選擇工程 —</option>
            {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} · {o.title || o.customer_name}</option>)}
          </select>
        </Field>
        <Field label="供應商 Supplier" required>
          <select className="select" value={f.supplier_id} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">— 選擇供應商 —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="分判日期"><input className="input" type="date" value={f.po_date} onChange={e => set('po_date', e.target.value)} /></Field>
        <Field label="交貨日期"><input className="input" type="date" value={f.delivery_date} onChange={e => set('delivery_date', e.target.value)} /></Field>
      </div>

      <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📋</span>分判明細</div>
      <ItemsEditor items={f.items} setItems={v => set('items', v)} itemsCatalog={catalog} />

      <div className="card-title mt"><span className="ico-badge amber" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📎</span>
        上傳 PO 原件 (PDF / 相片) — 例如已在其他系統開好嘅 PO
        <span className="spacer" />
        <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple style={{ display: 'none' }} onChange={pickFile} />
        <button className="btn sm primary" onClick={() => fileRef.current?.click()}>📎 選擇檔案</button>
      </div>
      <div className="att-grid mb">
        {existAtts.map(a => (
          <div key={a.id} className="att-card">
            <a href={`/uploads/${a.stored_name}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="att-thumb">{a.mime?.startsWith('image/') ? <img src={`/uploads/${a.stored_name}`} alt={a.filename} /> : '📄'}</div>
              <div className="att-name">{a.filename}</div>
            </a>
            <button className="btn sm coral" style={{ marginTop: 8 }} onClick={() => delAtt(a)}>刪除</button>
          </div>
        ))}
        {files.map((fl, i) => (
          <div key={i} className="att-card" style={{ borderStyle: 'solid', borderColor: 'var(--primary-lt)' }}>
            <div className="att-thumb">{fl.mime.startsWith('image/') ? <img src={fl.data_base64} alt={fl.filename} /> : '📄'}</div>
            <div className="att-name">{fl.filename}</div>
            <button className="btn sm coral" style={{ marginTop: 8 }} onClick={() => setFiles(l => l.filter((_, x) => x !== i))}>移除</button>
          </div>
        ))}
      </div>

      <div className="form-grid">
        <Field label="直接填寫分判總額 (上傳 PO 用，留空則按明細計算)">
          <input className="input num-input" type="number" step="0.01" placeholder={`按明題計: ${money(subtotal)}`}
            value={f.total_override} onChange={e => set('total_override', e.target.value)} disabled={false} />
        </Field>
        <Field label="狀態">
          <select className="select" value={f.status} onChange={e => set('status', e.target.value)}>
            {Object.entries(PO_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="備註" span2><input className="input" value={f.notes} onChange={e => set('notes', e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

export default function PurchaseOrders() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editor, setEditor] = useState(null); // {} | {id}
  const [q, setQ] = useState('');
  const refresh = () => api.get(`/pos?${q ? `q=${encodeURIComponent(q)}` : ''}`).then(setRows);
  useEffect(() => {
    refresh();
    api.get('/orders').then(setOrders);
    api.get('/suppliers').then(setSuppliers);
    api.get('/items').then(setCatalog);
    const po = params.get('order');
    if (po) setEditor({ presetOrder: Number(po) });
  }, [q]);
  const openEdit = async (id) => {
    const d = await api.get(`/pos/${id}`);
    setEditor(d);
  };
  const del = (row) => confirm(`刪除分判商單 ${row.po_no}？`, async () => {
    try { await api.del(`/pos/${row.id}`); toast('已刪除'); refresh(); } catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '刪除' });
  const toPDF = async (row) => {
    try {
      toast('正在產生 PDF…');
      const resp = await fetch('/api/pos/' + row.id + '/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗 (' + resp.status + ')');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = row.po_no + '.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast('PDF 已下載 ✔');
    } catch (e) { toast(e.message, 'err'); }
  };
  const totals = (rows || []).filter(p => p.status !== 'cancelled').reduce((a, p) => a + p.total, 0);

  return (
    <div>
      <PageHead icon="📦" title="分判商單 Sub-contractors" desc={`分判商單 — 每張 PO 必須關聯工程 · 總分判額 HK$ ${money(totals)}`}
        actions={<>
          <div className="search-box"><input className="input" placeholder="搜尋分判商單/供應商/工程…" value={q} onChange={e => setQ(e.target.value)} /></div>
          <button className="btn primary" onClick={() => setEditor({})}>➕ 新分判商單</button>
        </>} />
      <div className="card">
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty icon="📦" text="沒有分判商單">
          <button className="btn primary mt" onClick={() => setEditor({})}>➕ 開第一張 PO</button>
        </Empty> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>PO 編號</th><th>供應商</th><th>掛單工程</th><th>日期</th><th className="num">金額</th><th className="num">供應商發票</th><th>附件</th><th>狀態</th><th></th></tr></thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id} className="clickable" onClick={() => openEdit(p.id)}>
                    <td className="strong">{p.po_no}</td>
                    <td>{p.supplier_name}</td>
                    <td><span className="cat-tag cat-ins">{p.order_no || '—'}</span><div className="sub">{p.order_title}</div></td>
                    <td>{fmtDate(p.po_date)}</td>
                    <td className="num strong">{money(p.total)}</td>
                    <td className="num">{p.si_amount ? money(p.si_amount) : '—'}</td>
                    <td>{p.attachment_count ? `📎 ${p.attachment_count}` : '—'}</td>
                    <td><StatusBadge map={PO_STATUS} value={p.status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <button className="btn sm" onClick={() => toPDF(p)}>📥</button>
                        <button className="btn sm coral" onClick={() => del(p)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editor && (
        <POEditor orders={orders} suppliers={suppliers} catalog={catalog}
          initial={editor.id ? editor : {}}
          presetOrder={editor.presetOrder}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); refresh(); }} />
      )}
      {confirmNode}
    </div>
  );
}
